import Button from '@/components/atoms/Button';
import Heading from '@/components/atoms/Heading';
import RouterBackButton from '@/components/atoms/RouterBackButton';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';
import ApplicationErrorDialog from '@/mobile/components/organisms/ApplicationErrorDialog';
import CollectionHopeDate from '@/mobile/components/organisms/contract/quit/CollectionHopeDate';
import CollectionAddress, {
  CollectionAddressSelectorRef,
} from '@/mobile/components/organisms/contract/quit/rentalwifi/CollectionAddressSelector';
import QuitTextProvider from '@/mobile/components/organisms/quit/QuitTextProvider';
import QuitTextMobileRentalWifi from '@/mobile/components/organisms/quit/texts/QuitTextMobileRentalWifi';
import useGetQuitInfo from '@/mobile/hooks/api/useGetQuitInfo';
import { QuitInfoState } from '@/mobile/states/mobileQuitInfoState';
import { CollectionHopeDateType } from '@/mobile/types/api/mypage/collectionHopeDate';
import { yupResolver } from '@hookform/resolvers/yup';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';
import * as yup from 'yup';

/**
 * 「解約受付」の「初期画面」の画面コンポーネント。
 * @returns 画面コンポーネント
 */
const QuitEntryIndex: NextPage = () => {
  const router = useRouter();
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const collectionAddressRef = useRef<CollectionAddressSelectorRef>(null);
  const { execute: callAPIGetQuitInfo } = useGetQuitInfo();
  const QuitInfo = useRecoilValue(QuitInfoState);

  useEffect(() => {
    // 解約可否や分岐条件は契約状態に強く依存するため、初期表示時に必ず最新情報を取り直す。
    // モバイル解約受付情報取得API呼び出し
    callAPIGetQuitInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // フォームスキーマの定義
  const schema = yup.object().shape({
    collectionHopeDate: yup.string(),
    collectionHopeTimezone: yup.string(),
  });

  // フォームの設定
  const useFormReturn = useForm<CollectionHopeDateType>({
    resolver: yupResolver(schema),
  });

  // レンタルWiFi解約ボタンクリック時の処理
  const handleRentalWifiQuitClick = () => {
    // 注意事項確認前に次画面へ進ませると、解約確定の前提条件を満たさないまま登録に進んでしまう。
    if (!isConfirmed) return;

    // 住所データをRecoilステートに保存
    if (collectionAddressRef.current) {
      const selectedOption = collectionAddressRef.current.getSelectedOption();

      if (selectedOption === 'custom') {
        // 手入力住所は確認画面でそのまま再利用するため、この時点で整合性を担保しておく。
        // カスタム住所の場合はバリデーションを実行
        const isValid = collectionAddressRef.current.validateForm(true);
        if (!isValid) {
          // バリデーションエラーがある場合は確認画面に遷移しない
          return;
        }
      } else {
        // 既定住所選択時も API 送信用ステートを同期させ、確認画面の表示と送信内容を一致させる。
        // 送付先住所の場合もRecoilステートを更新
        collectionAddressRef.current.validateForm(false);
      }
    }

    // レンタルWiFiだけは住所と集荷希望日の確認が必要なため、専用確認画面へ分岐する。
    // 確認画面に遷移
    router.push('/mobile/contract/quit/confirmRentalWifi');
  };

  return (
    <>
      {QuitInfo.can_display && (
        <main className='l-main'>
          <Heading as='h1'>解約のお手続き</Heading>
          <div className='m-box-02'>
            <div className='m-box-02__inner'>
              <div className='m-text'>
                <p>ご解約のお手続きに関する注意事項をご確認ください。</p>
              </div>
              {!QuitInfo.can_quit_rental_wifi && (
                <div className='m-text'>
                  <p>※記載の金額はすべて税込です。</p>
                </div>
              )}
              <Heading as='h2'>注意事項</Heading>
              {/* レンタルWifi以外の注意事項 */}
              {!QuitInfo.can_quit_rental_wifi && (
                <QuitTextProvider identifier={QuitInfo.contract_type} />
              )}
              {/* レンタルWifiの注意事項 */}
              {QuitInfo.can_quit_rental_wifi && <QuitTextMobileRentalWifi />}
              {/* レンタルWifiの集荷先住所指定 */}
              {QuitInfo.can_quit_rental_wifi && <CollectionAddress ref={collectionAddressRef} />}
              {/* レンタルWifiの場合、集荷希望指定 */}
              {QuitInfo.can_quit_rental_wifi && <CollectionHopeDate {...useFormReturn} />}

              <div className='m-form-conversion'>
                <div className='m-form-checkbox'>
                  <input
                    className='mgr-10px'
                    type='checkbox'
                    id='agreement'
                    checked={isConfirmed}
                    onChange={(): void => {
                      setIsConfirmed(!isConfirmed);
                    }}
                  />
                  <label className='m-text' htmlFor='agreement'>
                    すべてのご注意事項を確認しました
                  </label>
                </div>
              </div>
              {QuitInfo.can_quit_mnp && (
                <div className='m-button col1'>
                  <Button
                    variant='secondary'
                    type='submit'
                    onClick={() => {
                      // MNP は通常解約と確認事項が異なるため、専用確認画面へ分ける。
                      router.push('/mobile/contract/quit/confirmMnp');
                    }}
                    disabled={!isConfirmed}
                  >
                    MNP転出による解約
                  </Button>
                </div>
              )}
              {QuitInfo.can_quit_normal && (
                <div className='m-button col1'>
                  <Button
                    variant='secondary'
                    type='submit'
                    onClick={() => {
                      // 月末解約は解約予定日の表示確認が必要なため、通常解約用の確認画面を経由させる。
                      router.push('/mobile/contract/quit/confirm');
                    }}
                    disabled={!isConfirmed}
                  >
                    月末解約
                  </Button>
                </div>
              )}
              {QuitInfo.can_quit_rental_wifi && (
                <div className='m-button col1'>
                  <Button
                    variant='secondary'
                    type='submit'
                    onClick={handleRentalWifiQuitClick}
                    disabled={!isConfirmed}
                  >
                    解約
                  </Button>
                </div>
              )}

              <div className='m-button col1'>
                <RouterBackButton>戻る</RouterBackButton>
              </div>
            </div>
          </div>
          <BreadcrumbLists />
        </main>
      )}

      <ApplicationErrorDialog
        message={
          <p>
            データ前借をご利用されたため、今月中は解約のお申し込みを受付できません。
            <br />
            解約をご希望の場合は、翌月1日以降にデータ前借をご利用されていない状態で再度お申し込みください。
          </p>
        }
      />
    </>
  );
};

export default QuitEntryIndex;
